import React, { useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  FiHome as Home,
  FiFileText as FileText,
  FiCreditCard as CreditCard,
  FiPhone as Phone,
  FiDollarSign as DollarSign,
  FiUsers as Users,
  FiSettings as Settings,
  FiLogOut as LogOut,
  FiUser as User,
  FiBarChart2 as BarChart2,
  FiFolder as Folder,
  FiX as X
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

export const navigationItems = [
  { path: '/dashboard', icon: Home, label: 'Dashboard', exact: true },
  { path: '/dashboard/policies', icon: FileText, label: 'My Policies' },
  { path: '/dashboard/comparison', icon: BarChart2, label: 'Compare Policies' },
  { path: '/dashboard/documents', icon: Folder, label: 'Documents' },
  { path: '/dashboard/id-cards', icon: CreditCard, label: 'ID Cards' },
  { path: '/dashboard/claims', icon: Phone, label: 'File a Claim' },
  { path: '/dashboard/payment', icon: DollarSign, label: 'Make a Payment' },
  { path: '/dashboard/referrals', icon: Users, label: 'Refer a Friend' },
  { path: '/dashboard/settings', icon: Settings, label: 'Settings' }
];

/**
 * Full navigation menu. Pinned to the left on large screens; on phones it
 * slides in as a drawer opened from the bottom tab bar's "More" button.
 */
const Sidebar = ({ isOpen = false, onClose = () => {} }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Don't let the page scroll behind an open drawer.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // Close the drawer on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const panel = (
    <div className="flex flex-col h-full">
      {/* Drawer-only header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 lg:hidden">
        <span className="font-bold text-gray-900">Menu</span>
        <button
          onClick={onClose}
          aria-label="Close menu"
          className="p-2 -mr-2 text-gray-500 hover:text-gray-900"
        >
          <X size={22} />
        </button>
      </div>

      <nav className="flex-1 p-4 lg:p-6 overflow-y-auto">
        <div className="space-y-1 lg:space-y-2">
          {navigationItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-gray-100 text-[#DC143C] border-r-4 border-[#DC143C]'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-[#DC143C]'
                }`
              }
            >
              <item.icon size={20} />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* User Profile Card */}
      <div className="p-4 lg:p-6 border-t border-gray-200">
        <div className="bg-gradient-to-br from-[#DC143C] to-[#B01030] rounded-lg p-4 text-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <User size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="text-sm text-white/80 truncate">
                {user?.email}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full bg-white/10 hover:bg-white/20 text-white py-2 px-3 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 text-sm font-medium"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop: pinned column */}
      <div className="hidden lg:block fixed left-0 top-20 bottom-0 w-280 bg-white shadow-lg border-r border-gray-200 z-30">
        {panel}
      </div>

      {/* Mobile: slide-in drawer */}
      <div
        className={`lg:hidden fixed inset-0 z-50 ${isOpen ? '' : 'pointer-events-none'}`}
        aria-hidden={!isOpen}
      >
        <div
          onClick={onClose}
          className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          className={`absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-white shadow-2xl transition-transform duration-200 ease-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Contents are unmounted while the drawer is closed.
              aria-hidden + pointer-events-none hid it from the accessibility
              tree and the mouse, but left all 11 links and buttons in the tab
              order: Tab walked focus into an off-screen drawer (WCAG 4.1.2).
              Focusability is the bug, so the elements have to be gone. */}
          {isOpen ? panel : null}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
